import { type JobSalary, createFuseSearchJobSalary } from "@lossdog/job-watchlists";
import { Button, Typography } from "@povio/ui";
import type { ColumnDef, Header, SortingState, Table } from "@tanstack/react-table";
import { cx } from "class-variance-authority";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useMeasure from "react-use-measure";

import { ArrowDropDownIcon } from "@/assets/icons/general/ArrowDropDown";
import { CancelIcon } from "@/assets/icons/general/CancelIcon";
import { SearchIcon } from "@/assets/icons/general/Search";
import { IconButton } from "@/components/ui/buttons/IconButton/IconButton";
import { Autocomplete } from "@/components/ui/inputs/Autocomplete/Autocomplete";
import { Dropdown } from "@/components/ui/inputs/Dropdown/Dropdown";
import { Menu, type MenuItem } from "@/components/ui/Menu/Menu";
import CustomTooltipTrigger from "@/components/ui/overlays/Tooltip/CustomTooltipTrigger";
import { TooltipEllipsis } from "@/components/ui/overlays/Tooltip/TooltipEllipsis";
import type { SortParams } from "@/components/ui/table/Table";
import TableCell from "@/components/ui/table/TableCell";
import { VirtualizedTable } from "@/components/ui/table/VirtualizedTable";
import WatchlistTableHeaderCell from "@/components/watchlist/components/WatchlistTableHeaderCell";
import { useJobSalaries } from "@/components/watchlist/hooks/useJobSalaries";
import { JobSalaryNetChange } from "@/components/watchlist/job-salary-ticker/JobSalaryNetChange";
import { TourSelectors } from "@/config/tour.config";
import type { WatchlistModels } from "@/data/watchlist/watchlist.models";
import { useTour } from "@/hooks/useTour";
import { DemoContext } from "@/providers/demo/DemoContext";

import { JobSalaryLastPrice } from "./job-salary-ticker/JobSalaryLastPrice";
import { JobSalaryYearlyChangesValue } from "./job-salary-ticker/JobSalaryYearlyChanges";

const TOP_LOCATIONS = ["chicago", "new york", "san francisco", "los angeles"];

type JobSalaryRow = { jobSalary: JobSalary; fitScore: number | null };

export const sortByToState = (sortBy: SortParams["sortBy"] | undefined) => {
  if (!sortBy) return [];

  const [id, desc] = sortBy.split(".");
  return [{ id, desc: desc === "desc" }];
};

export const stateToSortBy = (sorting: SortingState | undefined) => {
  if (!sorting || sorting.length === 0) return undefined;

  const sort = sorting[0];

  return `${sort.id}.${sort.desc ? "desc" : "asc"}` as const;
};
interface IProps {
  jobSalaryChanged: () => void;
  isDesktop?: boolean;
  jobFits?: WatchlistModels.WatchlistJobFitListResponseDto | null;
}
export const WatchlistTable = ({ jobSalaryChanged, isDesktop = true, jobFits }: IProps) => {
  const { t } = useTranslation();
  const [triggerRef, { width }] = useMeasure();
  const { isDemoMode } = DemoContext.useDemo();
  const { isTourActive = false } = useTour();
  const [jobPositionSearch, setJobPositionSearch] = useState<string>();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const {
    jobSalaries,
    allJobSalaries,
    watchlistGroups,
    metroAreas,
    quantiles,
    selectedWatchlistGroup,
    selectedWatchlist,
    selectedMetroArea,
    selectedQuantile,
    setSelectedWatchlistGroup,
    setSelectedMetroArea,
    setSelectedWatchlist,
    setSelectedQuantile,
    isLoading,
    setSelectedJobSalary,
  } = useJobSalaries();

  const jobSearcher = useMemo(() => {
    return createFuseSearchJobSalary(allJobSalaries ?? []);
  }, [allJobSalaries]);

  const filteredJobs = useMemo(() => {
    let filtered = allJobSalaries ?? [];

    // Apply search filter
    const searchTermToUse = jobPositionSearch?.trim();
    if (searchTermToUse) {
      filtered = jobSearcher(searchTermToUse);
    }

    // Limit to 50 results
    return filtered.slice(0, 50);
  }, [allJobSalaries, jobSearcher, jobPositionSearch]);

  const tableData: JobSalaryRow[] = useMemo(() => {
    const items = jobFits?.items ?? [];
    const fitMap = new Map(items.map((item) => [item.jobTitle, item.fitScore]));
    return (jobSalaries ?? []).map((jobSalary) => ({
      jobSalary,
      fitScore: fitMap.get(jobSalary.title) ?? null,
    }));
  }, [jobSalaries, jobFits?.items]);

  useEffect(() => {
    if (!firstLoad) {
      return;
    }
    // Only auto-select first job on desktop screens
    if (isDesktop && jobSalaries && jobSalaries.length > 0) {
      setSelectedJobSalary(jobSalaries[0] ?? null);
      setFirstLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobSalaries, firstLoad, isDesktop]);

  // Wrapper functions to handle type conversion for dropdowns
  const handleQuantileChange = (item: { id: string; title: string } | null) => {
    if (item) {
      // Find the full Quantile object from the quantiles array
      const fullQuantile = quantiles?.find((q) => q.id === item.id);
      setSelectedQuantile(fullQuantile || null);
    } else {
      setSelectedQuantile(null);
    }
  };

  const handleMetroAreaChange = (item: { id: string; title: string } | null) => {
    if (item) {
      // Find the full MetroArea object from the metroAreas array
      const fullMetroArea = metroAreas?.find((m) => m.id === item.id);
      setSelectedMetroArea(fullMetroArea || null);
    } else {
      setSelectedMetroArea(null);
    }
  };

  const [sorting, setSorting] = useState<SortingState>([{ id: "title", desc: false }]);

  // Overlay state (commented out - using dropdowns instead)
  // const [isWatchlistGroupOverlayOpen, setIsWatchlistGroupOverlayOpen] = useState(false);
  // const [isWatchlistOverlayOpen, setIsWatchlistOverlayOpen] = useState(false);

  const columns: ColumnDef<JobSalaryRow>[] = [
    {
      id: "title",
      accessorFn: (row) => row.jobSalary.title,
      header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.position"), table, "start"),
      size: 170,
      cell: (info) => WatchlistTableCells(info, "title"),
      meta: { label: t("watchlist.filters.position") },
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.jobSalary.title.toLowerCase();
        const b = rowB.original.jobSalary.title.toLowerCase();
        return a.localeCompare(b);
      },
    },
    {
      id: "salary",
      accessorFn: (row) => row.jobSalary,
      header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.salary"), table, "end"),
      size: 80,
      cell: (info) => WatchlistTableCells(info, "salary"),
      meta: { label: t("watchlist.filters.salary") },
    },
    {
      id: "netChange",
      accessorFn: (row) => row.jobSalary,
      header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.progress"), table, "end"),
      size: 50,
      cell: (info) => WatchlistTableCells(info, "netChange"),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.jobSalary.netChange.value || 0;
        const b = rowB.original.jobSalary.netChange.value || 0;
        return a - b;
      },
      meta: { label: t("watchlist.filters.progress") },
    },
    {
      id: "netChangeOneYear",
      accessorFn: (row) => row.jobSalary,
      header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.1y"), table, "end"),
      size: 80,
      cell: (info) => WatchlistTableCells(info, "netChangeOneYear"),
      meta: { label: t("watchlist.filters.1y") },
    },
    {
      id: "netChangeThreeYear",
      accessorFn: (row) => row.jobSalary,
      header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.3y"), table, "end"),
      size: 80,
      cell: (info) => WatchlistTableCells(info, "netChangeThreeYear"),
      meta: { label: t("watchlist.filters.3y") },
    },
    {
      id: "netChangeFiveYear",
      accessorFn: (row) => row.jobSalary,
      header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.5y"), table, "end"),
      size: 80,
      cell: (info) => WatchlistTableCells(info, "netChangeFiveYear"),
      meta: { label: t("watchlist.filters.5y") },
    },
    {
      id: "averageTenureInYears",
      accessorFn: (row) => row.jobSalary.averageTenureInYears.value,
      header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.jobTenure"), table, "end"),
      size: 60,
      cell: (info) => WatchlistTableCells(info, "averageTenureInYears"),
      meta: { label: t("watchlist.filters.jobTenure") },
    },
    {
      id: "liquidity",
      accessorFn: (row) => row.jobSalary.liquidity.value,
      header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.liquidity"), table, "end"),
      size: 50,
      cell: (info) => WatchlistTableCells(info, "liquidity"),
      meta: { label: t("watchlist.filters.liquidity") },
    },
    {
      id: "fit",
      accessorKey: "fitScore",
      header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.fit", "Fit"), table, "end"),
      size: 40,
      cell: (info) => WatchlistTableCells(info, "fit"),
      meta: { label: t("watchlist.filters.fit", "Fit") },
    },
  ];

  const sortedTopLocations = useMemo(() => {
    const sortedMetroAreas: { id: string; label: string }[] = [];
    const sortedAlphabetically = metroAreas?.sort((a, b) => a.title.localeCompare(b.title));
    sortedAlphabetically?.forEach((metroArea) => {
      const isTopLocation = TOP_LOCATIONS.some((location) => metroArea.title.toLowerCase().includes(location));
      if (isTopLocation) {
        sortedMetroAreas.unshift({ id: metroArea.title, label: metroArea.title });
      } else {
        sortedMetroAreas.push({ id: metroArea.title, label: metroArea.title });
      }
    });

    return sortedMetroAreas;
  }, [metroAreas]);

  const menuItems: MenuItem[] = useMemo(() => {
    return [
      ...(watchlistGroups?.map((group) => ({
        label: group.title,
        content: group.title,
        onAction:
          group.title === "All"
            ? () => {
                setSelectedWatchlistGroup(group);
                setSelectedWatchlist(null);
              }
            : undefined,
        children:
          group.title !== "All" &&
          group.watchlists.map((watchlist: any) => ({
            label: watchlist.title,
            content: watchlist.title,
            onAction: () => {
              setSelectedWatchlistGroup(group);
              setSelectedWatchlist(watchlist);
            },
          })),
      })) || []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlistGroups]);

  const defaultTableMinHeight = useMemo(() => {
    if (tableData.length > 0) {
      const minHeight = tableData.length > 10 ? 10 * 44 : tableData.length * 44;
      return minHeight;
    }
    return 90;
  }, [tableData.length]);

  return (
    <div className="flex h-full w-full flex-[1_1_0] flex-col flex-wrap">
      <div className="flex w-full flex-[1_1_0] flex-col items-center justify-center">
        <div
          className={cx(
            "flex w-full flex-[0_0_0] flex-col items-center justify-center gap-4 dd:px-0 px-2",
            isDemoMode && "pt-1 t:pt-0",
          )}
        >
          <div className="flex w-full max-w-[1300px] flex-row flex-wrap items-center justify-between">
            <div className="flex w-full flex-col">
              {isSearchExpanded ? (
                <div className="flex w-full flex-row items-center gap-1">
                  <Autocomplete
                    key="job-select"
                    id={TourSelectors.SearchJobPositionInput}
                    items={filteredJobs?.map((position) => ({ id: position.id, label: position.title })) || []}
                    onSelectionChange={(value) => {
                      if (!value) {
                        return;
                      }
                      const job = filteredJobs.find((job) => job.id === value.toString()) || null!;
                      setSelectedJobSalary(job);
                      jobSalaryChanged();
                      setIsSearchExpanded(false);
                    }}
                    label=""
                    allowsCustomValue={false}
                    menuTrigger="focus"
                    autoFocus
                    resetInputAfterSelection={true}
                    onSearchChange={(value) => setJobPositionSearch(value)}
                    isLoading={isLoading}
                    isDisabled={isDemoMode && isTourActive}
                    isRequired
                    defaultFilter={() => true}
                    inputIcon={SearchIcon}
                    inputIconPosition="left"
                    hideLabels
                    placeholder={t("watchlist.search_placeholder", "Search Jobs")}
                    variant="search"
                    className="w-full"
                    inputClassname="h-[42px]! border-b-0! border-elevation-outline-default-1! rounded-none! truncate font-body-prominent-1! font-inter! text-text-default-1!"
                    placement="bottom start"
                    shouldFlip={false}
                    comboboxClassName="grow-1"
                  />
                  <IconButton
                    label="cancel"
                    icon={CancelIcon}
                    variant="transparent"
                    iconClassName="w-6 text-text-default-1"
                    className="h-[42px]! w-11! border-elevation-outline-default-1! p-0!"
                    onPress={() => setIsSearchExpanded(false)}
                  />
                </div>
              ) : (
                <div className="flex w-full flex-row items-center">
                  <IconButton
                    label="search"
                    icon={SearchIcon}
                    variant="transparent"
                    iconClassName="w-6 text-text-default-1"
                    className="h-[42px]! w-11! rounded-none! border border-elevation-outline-default-1! border-r-0! border-b-0! p-0!"
                    onPress={() => setIsSearchExpanded(true)}
                    id={TourSelectors.SearchJobPositionInput}
                  />
                  <Menu
                    breakpoint="xl"
                    items={menuItems}
                    id={TourSelectors.WatchlistGroupMenu}
                    popoverProps={{ className: "max-h-[80dvh] overflow-y-auto" }}
                    triggerClassName="max-w-unset"
                    trigger={
                      <Button
                        icon={ArrowDropDownIcon}
                        variant="outlined"
                        color="secondary"
                        isDisabled={isDemoMode && isTourActive}
                        iconPosition="right"
                        className="[&>span]:justify-between! [&>span>span]:truncate! justify-between! h-[42px]! w-full flex-1 rounded-none! border border-elevation-outline-default-1! border-r-0! border-b-0! pr-4! [&>span>span]:font-body-prominent-1! [&>span>span]:text-label-1!"
                        iconClassName="w-6 text-text-default-1"
                      >
                        {`${selectedWatchlistGroup?.title === "All" ? "All" : ""} ${selectedWatchlistGroup?.title !== "All" ? `${selectedWatchlistGroup?.title} > ${selectedWatchlist?.title}` : ""}`}
                      </Button>
                    }
                  />
                </div>
              )}

              <div className="flex w-full flex-row items-center">
                {metroAreas && selectedMetroArea && (
                  <Autocomplete
                    items={sortedTopLocations}
                    label=""
                    onSelectionChange={(item) => {
                      const metroArea = metroAreas.find((m) => m.title === item);
                      if (metroArea) {
                        handleMetroAreaChange(metroArea);
                      }
                    }}
                    selectedKey={selectedMetroArea?.title}
                    defaultSelectedKey={selectedMetroArea?.title}
                    inputValue={selectedMetroArea?.title}
                    inputClassname="h-[42px]! border-elevation-outline-default-1! rounded-none! truncate font-body-prominent-1! font-inter! text-text-default-1!"
                    inputIconClassname="text-text-default-1"
                    menuTrigger="focus"
                    variant="search"
                    placeholder="Select Metro Area"
                    isDisabled={isDemoMode && isTourActive}
                    hideLabels
                    comboboxClassName="w-1/2"
                  />
                )}
                {quantiles && (
                  <Dropdown
                    label=""
                    placeholder=""
                    hideLabels
                    containerClassName="w-1/2"
                    items={quantiles?.map((quantile) => ({ id: quantile.id, label: quantile.short })) || []}
                    value={selectedQuantile?.id || undefined}
                    customTriggerWidth={width}
                    customTrigger={
                      <div ref={triggerRef}>
                        <Button
                          icon={ArrowDropDownIcon}
                          isDisabled={isDemoMode && isTourActive}
                          variant="outlined"
                          color="secondary"
                          iconPosition="right"
                          size="s"
                          className="[&>span]:justify-between! justify-between! h-[42px] w-1/2 rounded-none! border border-elevation-outline-default-1! border-r-0! border-l-0! bg-interactive-outlined-secondary-idle! px-4! py-0 font-body-prominent-1! text-text-default-1! [&>span>span]:text-label-1!"
                          iconClassName="w-6 text-text-default-1"
                          width="fill"
                        >
                          {selectedQuantile?.short ?? ""}
                        </Button>
                      </div>
                    }
                    onChange={(e) => {
                      const newQuantile = quantiles?.find((q) => q.id === e);
                      if (!newQuantile) {
                        return;
                      }
                      handleQuantileChange(newQuantile);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <VirtualizedTable
          data={tableData}
          columns={columns}
          sorting={sorting}
          onSortingChange={setSorting}
          isLoading={isLoading}
          manualSorting={false}
          className="h-full w-full flex-1"
          tableStyle={{ minHeight: `${defaultTableMinHeight}px` }}
          noBorder
          onRowDataUsed={(rowData) => rowData.jobSalary.subscribe()}
          onRowClick={(rowData) => {
            setSelectedJobSalary(rowData.jobSalary);
            jobSalaryChanged();
          }}
          rowHeight={32}
          flexibleColumnId="title"
          getRowId={(_row, index) => {
            // Use 1st row (index 0) on mobile, index 11 on desktop for tour
            if (isDesktop && index === 0) return TourSelectors.WatchlistTableRow;
            if (!isDesktop && index === 2) return TourSelectors.WatchlistTableRow;
            return undefined;
          }}
        />
      </div>
    </div>
  );
};

const WatchlistHeaderCell = (
  header: Header<any, unknown>,
  label: string,
  table: Table<JobSalaryRow>,
  align: "start" | "center" | "end",
) => {
  return (
    <WatchlistTableHeaderCell
      header={header}
      label={label}
      align={align}
      table={table}
    />
  );
};

const WatchlistTableCells = (
  info: { row: { original: JobSalaryRow; index: number }; getValue: () => unknown },
  key: string,
) => {
  const row = info.row.original;

  if (key === "title") {
    return (
      <TableCell
        align="start"
        className="h-full w-full"
      >
        <TooltipEllipsis
          text={row.jobSalary.title}
          offset={30}
          placement="top left"
        >
          {(onContentRef) => (
            <CustomTooltipTrigger className="relative w-auto truncate">
              <Typography
                ref={onContentRef}
                size="label-1"
                className="max-w-full truncate"
              >
                {row.jobSalary.title}
              </Typography>
            </CustomTooltipTrigger>
          )}
        </TooltipEllipsis>
      </TableCell>
    );
  }

  if (key === "fit") {
    const score = row.fitScore;
    const display = score !== null ? String(score) + "%" : "-";
    const isHighFit = score !== null && score > 80;
    const textClass = isHighFit ? "text-interactive-contained-primary-idle" : "text-text-default-3";
    return (
      <TableCell
        align="end"
        className="h-full"
      >
        <Typography
          size="label-1"
          className={cx("text-right", textClass)}
        >
          {display}
        </Typography>
      </TableCell>
    );
  }

  if (key === "netChange") {
    return (
      <TableCell
        className="flex h-full flex-row items-center gap-2"
        align="end"
        id={info.row.index === 0 ? TourSelectors.WatchlistTableLastColumn : undefined}
      >
        <Typography
          size="label-1"
          className="text-right font-weight-600"
        >
          <JobSalaryNetChange jobSalary={row.jobSalary} />
        </Typography>
      </TableCell>
    );
  }

  if (key === "salary") {
    return (
      <TableCell
        align="end"
        className="h-full"
      >
        <Typography
          size="label-1"
          className="text-right"
        >
          <JobSalaryLastPrice jobSalary={row.jobSalary} />
        </Typography>
      </TableCell>
    );
  }

  if (key === "netChangeFiveYear" || key === "netChangeThreeYear" || key === "netChangeOneYear") {
    return (
      <TableCell
        align="end"
        className="h-full"
      >
        <Typography
          size="label-1"
          className="text-right"
        >
          <JobSalaryYearlyChangesValue
            propKey={key}
            jobSalary={row.jobSalary}
          />
        </Typography>
      </TableCell>
    );
  }

  if (key === "averageTenureInYears") {
    const rawValue = info.getValue();
    const value = Number.isNaN(rawValue) ? "-" : rawValue;

    return (
      <TableCell
        align="end"
        className="h-full"
      >
        <Typography
          size="label-1"
          className="w-80 min-w-20 truncate text-right"
        >
          {value as number}
        </Typography>
      </TableCell>
    );
  }

  return (
    <TableCell
      align="end"
      className="h-full"
    >
      <Typography
        size="label-1"
        className="w-80 min-w-20 truncate text-right"
      >
        {info.getValue() as string}
      </Typography>
    </TableCell>
  );
};
